import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { IPlayer, IFormFields } from 'pages/Game/Game';
import { useGame } from 'context/GameContext';
import { createDefaultScores } from 'utils/game';

interface PlayerContextType {
  players: IPlayer[];
  updatePlayers: (data: IFormFields) => void;
  updateClassicScore: (score: number) => void;
  updateDartsScore: (score: number) => void;
  undoScore: () => void;
  backToGame: () => void;
  togglePlayerActive: (index: number) => void;
  handleAdditionalPlayer: (newPlayer: IPlayer) => void;
  editPlayerName: (index: number, newName: string) => void;
  popoverMessage?: string;
}

const PlayerContext = createContext<PlayerContextType | undefined>(undefined);

export const PlayerProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [players, setPlayers] = useState<IPlayer[]>([]);
  const [popoverMessage, setPopoverMessage] = useState<string | undefined>();
  const {
    currentTurn,
    currentRound,
    nextTurn,
    undoTurn,
    gameSettings,
    handleBackToGame,
    setIsGameFinished,
    setIsFinalRound,
    isFinalRound,
  } = useGame();

  const playTone = (frequency: number) => {
    if (!gameSettings.audioStatus) {
      return;
    }

    const AudioContextClass =
      window.AudioContext || (window as any).webkitAudioContext;
    const context = new AudioContextClass();
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();

    oscillator.frequency.value = frequency;
    gainNode.gain.value = 0.04;

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);

    oscillator.start();
    oscillator.stop(context.currentTime + 0.07);
  };

  const playAddScoreSound = () => playTone(280);
  const playUndoScoreSound = () => playTone(150);

  const updatePlayers = (data: IFormFields) => {
    setPlayers(
      data.players.map((player) => ({
        ...player,
        scores: createDefaultScores(data.rounds),
        isActive: true,
      }))
    );
  };

  const editPlayerName = (index: number, newName: string) => {
    setPlayers((prev) => {
      const updated = [...prev];
      updated[index].player = newName;
      sessionStorage.setItem('gameData', JSON.stringify(updated));
      return updated;
    });
  };

  const togglePlayerActive = (index: number) => {
    setPlayers((prev) =>
      prev.map((player, i) =>
        i === index
          ? {
              ...player,
              isActive: !player.isActive,
              scores: player.isActive
                ? player.scores.map((item) => item || 0)
                : [...player.scores],
            }
          : player
      )
    );
  };

  const handleBustScore = (score: number) => {
    setPopoverMessage(`Bust! You scored ${score}, which is too much.`);
    setTimeout(() => {
      setPopoverMessage(undefined);
    }, 2500);
  };

  const handleAdditionalPlayer = (newPlayer: IPlayer) => {
    setPlayers((prev) => [...prev, newPlayer]);
  };

  const updateClassicScore = useCallback(
    (score: number) => {
      setPlayers((prev) => {
        playAddScoreSound();
        const updated = [...prev];
        const player = updated[currentTurn];

        if (player?.isActive) {
          player.scores[currentRound] = score;
          player.totalPoints = player.scores.reduce<number>(
            (sum, s) => sum + (s ?? 0),
            0
          );
        }

        sessionStorage.setItem('gameData', JSON.stringify(updated));

        return updated;
      });

      nextTurn(players.length);
    },
    [players, currentTurn, currentRound, nextTurn, playAddScoreSound]
  );

  const updateDartsScore = useCallback(
    (score: number) => {
      setPlayers((prev) => {
        const updated = [...prev];
        const player = updated[currentTurn];
        if (!player?.isActive) return prev;

        playAddScoreSound();

        const target = gameSettings.target ?? 0;
        const scoresBeforeCurrent = player.scores.slice(0, currentRound);

        const totalScoredBefore = scoresBeforeCurrent.reduce(
          (sum: number, s) => sum + (typeof s === 'number' ? s : 0),
          0
        );

        const remaining = target - (totalScoredBefore + score);
        const finalPlayerWithNoScore =
          players.length === currentTurn + 1 && remaining === 0;

        if (remaining < 0) {
          player.scores[currentRound] = undefined;
          player.totalPoints = target - totalScoredBefore;

          handleBustScore(score);
        } else {
          player.scores[currentRound] = score;
          player.totalPoints = remaining;
        }

        // Check if player finished by hitting exactly 0
        if (finalPlayerWithNoScore) {
          setIsGameFinished(true);
        } else if (remaining === 0) {
          setIsFinalRound(true);
          console.log('lol');
        }

        const isLastPlayer = currentTurn === updated.length - 1;

        if (isFinalRound && isLastPlayer) {
          setIsGameFinished(true);
        }

        sessionStorage.setItem('gameData', JSON.stringify(updated));
        return updated;
      });

      nextTurn(players.length);
    },
    [
      nextTurn,
      playAddScoreSound,
      gameSettings.target,
      gameSettings.totalRounds,
      currentTurn,
      currentRound,
      setPopoverMessage,
      setIsGameFinished,
      setIsFinalRound,
      isFinalRound,
    ]
  );

  const handleUndoTurn = () => {
    // Manually handle the turn and round updates without resetting prior scores
    let turn = currentTurn - 1;
    let round = currentRound;

    if (turn < 0) {
      turn = players.length - 1;
      round = Math.max(0, currentRound - 1);
    }

    undoTurn(turn, round);
  };

  const undoScore = () => {
    setIsFinalRound(false);
    playUndoScoreSound();
    setPlayers((prev) => {
      const updated = [...prev];
      const player = updated[currentTurn];

      if (player) {
        player.scores[currentRound] = undefined;
        player.totalPoints = player.scores.reduce<number>(
          (sum, s) => sum + (s ?? 0),
          0
        );
      }

      sessionStorage.setItem('gameData', JSON.stringify(updated));
      return updated;
    });

    handleUndoTurn();
  };

  const backToGame = () => {
    handleBackToGame();
    handleUndoTurn();
  };

  useEffect(() => {
    const savedGameData = sessionStorage.getItem('gameData');
    if (savedGameData) {
      setPlayers(JSON.parse(savedGameData));
    }
  }, []);

  return (
    <PlayerContext.Provider
      value={{
        players,
        updatePlayers,
        updateClassicScore,
        updateDartsScore,
        undoScore,
        togglePlayerActive,
        handleAdditionalPlayer,
        editPlayerName,
        backToGame,
        popoverMessage,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayers = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayers must be used within PlayerProvider');
  }

  return context;
};
